import React from 'react';
import {
  Box,
  TextField,
  Typography,
} from '@mui/material';
import { FILTER_FIELD_TYPES } from './filterTypes';
import MultiSelectDropdown from '../../../components/elements/MultiSelectDropdown';

/**
 * FieldLabel - Consistent label component for filters
 */
const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      color: '#344054',
      fontSize: '13px',
      fontWeight: 600,
      mb: 0.75,
      display: 'block',
      textTransform: 'none',
      letterSpacing: '0.01em',
    }}
  >
    {children}
    {required && <span style={{ color: '#d92d20', marginLeft: '2px' }}>*</span>}
  </Typography>
);

/**
 * Custom text/date field wrapper
 */
const FilterTextField = ({ label, required, ...props }) => (
  <Box sx={{ mb: 2 }}>
    <FieldLabel required={required}>{label}</FieldLabel>
    <TextField
      fullWidth
      size="small"
      {...props}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          fontSize: '14px',
          backgroundColor: '#fff',
          '& fieldset': { borderColor: '#D0D5DD' },
          '&:hover fieldset': { borderColor: '#98A2B3' },
          '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1.5px' },
        },
        ...props.sx
      }}
    />
  </Box>
);

const FilterField = ({ field, value, onChange, optionsMap = {} }) => {
  const { key, label, type, enumOptions, placeholder, renderCustom, required } = field;
  const options = optionsMap[key] || field.staticOptions || [];

  // Common change handler
  const handleValueChange = (v) => onChange(key, v);

  switch (type) {
    case FILTER_FIELD_TYPES.SELECT:
    case FILTER_FIELD_TYPES.ENUM: {
      const selectOptions = type === FILTER_FIELD_TYPES.ENUM
        ? (enumOptions || []).map(o => ({ id: o.value, name: o.label }))
        : options;

      return (
        <Box sx={{ mb: 0 }}>
          <FieldLabel required={required}>{label}</FieldLabel>
          <MultiSelectDropdown
            multiple={false}
            options={selectOptions}
            selectedValues={value ?? ''}
            onChange={handleValueChange}
            placeholder={placeholder || `Select ${label}`}
            sx={{ mb: 2 }}
          />
        </Box>
      );
    }

    case FILTER_FIELD_TYPES.MULTI_SELECT: {
      const validSelected = Array.isArray(value)
        ? value.filter((v) => options.some((o) => o.id === v || o.id === String(v)))
        : [];

      return (
        <Box sx={{ mb: 0 }}>
          <FieldLabel required={required}>{label}</FieldLabel>
          <MultiSelectDropdown
            multiple={true}
            searchable={options.length > 8}
            options={options}
            selectedValues={validSelected}
            onChange={handleValueChange}
            placeholder={placeholder || `Select ${label}`}
            sx={{ mb: 2 }}
          />
        </Box>
      );
    }

    case FILTER_FIELD_TYPES.TEXT:
      return (
        <FilterTextField
          label={label}
          required={required}
          placeholder={placeholder || `Search ${label}...`}
          value={value ?? ''}
          onChange={(e) => handleValueChange(e.target.value)}
        />
      );

    case FILTER_FIELD_TYPES.DATE:
      return (
        <FilterTextField
          label={label}
          required={required}
          type="date"
          value={value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
        />
      );

    case FILTER_FIELD_TYPES.DATE_RANGE:
      return (
        <Box sx={{ mb: 2 }}>
          <FieldLabel required={required}>{label}</FieldLabel>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={value?.from || ''}
              onChange={(e) => handleValueChange({ ...(value || {}), from: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '13px',
                  '& fieldset': { borderColor: '#D0D5DD' },
                }
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontSize: '12px' }}>to</Box>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={value?.to || ''}
              onChange={(e) => handleValueChange({ ...(value || {}), to: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '13px',
                  '& fieldset': { borderColor: '#D0D5DD' },
                }
              }}
            />
          </Box>
        </Box>
      );

    case FILTER_FIELD_TYPES.CUSTOM:
      return typeof renderCustom === 'function' 
        ? (
          <Box sx={{ mb: 2 }}>
            <FieldLabel required={required}>{label}</FieldLabel>
            {renderCustom({ value, onChange: handleValueChange })}
          </Box>
        )
        : <Typography variant="body2" color="error">Custom field "{key}" missing renderCustom.</Typography>;

    default:
      return null;
  }
};

export default FilterField;